<template>
  <div>
    <v-alert border="top" colored-border color="secondary" elevation="2">
      <h3 class="headline">Reports</h3>
      <p>
        Your reports are included below by category.
      </p>
      <div class="text-center pa-5" v-if="loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </v-alert>
    <div v-if="!loading">
      <v-row>
        <v-col xl="6" lg="6" md="6" sm="12" xs="12">
          <div v-if="customReports && customReports.length > 0">
            <template v-for="(item, i) in customReports">
              <v-card :key="i" :loading="loading" class="mb-4">
                <v-toolbar>
                  <v-toolbar-title>{{ item.text }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <div v-if="item.children">
                    <template v-for="(child, j) in item.children">
                      <v-list-item :key="j" link :to="child.to" two-line>
                        <v-list-item-content>
                          <v-list-item-title dense>
                            <span>{{ child.text }}</span>
                          </v-list-item-title>
                          <v-list-item-subtitle>{{ child.description }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </div>
          <v-card v-else :loading="loading">
            <v-toolbar>
              <v-toolbar-title>No Reports Configured</v-toolbar-title>
            </v-toolbar>
            <v-card-text>There are currently no reports configured</v-card-text>
          </v-card>
        </v-col>
        <v-col xl="6" lg="6" md="6" sm="12" xs="12">
          <div v-if="favoriteReports && favoriteReports.length > 0">
            <template>
              <v-card :loading="loading" class="mb-4">
                <v-toolbar>
                  <v-toolbar-title>Favorite Reports</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <v-list-item link :to="item.to" two-line v-for="(item, i) in favoriteReports" :key="i">
                    <v-list-item-content>
                      <v-list-item-title dense>
                        <span>{{ item.name }}</span>
                        &nbsp;
                        <v-tooltip bottom v-if="item.scheduled">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon right v-bind="attrs" v-on="on" color="primary">mdi-clock-outline</v-icon>
                          </template>
                          <span>To be delivered {{ item.scheduledCronExpression | toCronReadable }}</span>
                        </v-tooltip>
                        <v-tooltip
                          bottom
                          v-if="item.customReportFilterValues && item.customReportFilterValues.length > 0"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon right v-bind="attrs" v-on="on" color="primary">mdi-filter-outline</v-icon>
                          </template>
                          <span>
                            Filters:
                            <span
                              v-for="customReportFilter in item.customReport.customReportFilters"
                              :key="customReportFilter.id"
                            >
                              <br /><v-chip v-if="!isEmpty(customReportFilter.value)" class="mb-1">
                                <span>{{ customReportFilter.name }}</span>
                                <strong class="mx-1">{{ customReportFilter.filterType.matchingName }}</strong>
                                <span>({{ $customReport.displayFilterValue(customReportFilter) }})</span>
                              </v-chip>
                            </span>
                          </span>
                        </v-tooltip>
                      </v-list-item-title>
                      <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-card-text>
              </v-card>
            </template>
          </div>
          <v-card v-else :loading="loading">
            <v-toolbar>
              <v-toolbar-title>No Favorite Reports</v-toolbar-title>
            </v-toolbar>
            <v-card-text
              >To add a favorite report and potentially schedule an email delivery, open any report and mark it a
              favorite by clicking the <v-icon>mdi-heart-outline</v-icon> icon</v-card-text
            >
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Reports",
  metaInfo: {
    title: "Reports"
  },
  data() {
    return {
      loading: true,
      selected: null,
      customReports: [],
      customReportIds: [],
      favoriteReports: []
    };
  },
  methods: {
    toReportLink(report) {
      return {
        name: "report",
        params: { id: report.customReport.id },
        query: report.id ? { favorite: report.id } : {}
      };
    },
    fetchFavoriteReports() {
      if (this.customReportIds.length === 0) {
        return Promise.resolve();
      }

      this.loading = true;

      let postForm = {};
      postForm.participant = {
        id: this.selectedParticipant.id
      };
      postForm.customReports = this.customReportIds.map(item => {
        return { id: item };
      });

      const response = this.$api.post(
        "/api/customReportConfigurations/search?expand=customReport.customReportFilters&expand=customReportFilterValues.customReportFilter&sort=name&size=100",
        postForm
      );

      return response
        .then(({ data }) => {
          this.favoriteReports = data.content.map(report => ({
            ...report,
            to: this.toReportLink(report)
          }));

          // set customReportFilterValues for each favorite report
          this.favoriteReports.forEach(report => {
            report.customReport.customReportFilters.forEach(customReportFilter => {
              let customReportFilterValue = report.customReportFilterValues.find(
                customReportFilterValue => customReportFilterValue.customReportFilter.id === customReportFilter.id
              );
              if (customReportFilterValue) {
                customReportFilter.value = customReportFilterValue.selectedValue;
              } else {
                customReportFilter.value = null;
              }
            });
          });

          console.log(this.favoriteReports);
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    fetchCustomReports() {
      this.loading = true;
      return this.$api
        .get("/api/customReports/myReports")
        .then(({ data }) => {
          this.customReportIds = data.map(report => report.id);
          let reports = [];
          data
            .sort((a, b) => {
              // sort by category first, then by report
              if (a.category && b.category) {
                if (a.category.rankOrder == b.category.rankOrder) {
                  return a.rankOrder - b.rankOrder;
                } else {
                  return a.category.rankOrder - b.category.rankOrder;
                }
              } else {
                return a.rankOrder - b.rankOrder;
              }
            })
            .forEach(report => {
              if (!reports[report.category.name]) {
                reports[report.category.name] = [];
              }
              reports[report.category.name].push({
                text: report.name,
                description: report.description,
                to: {
                  name: "report",
                  params: { id: report.id }
                }
              });
            });

          this.customReports = [];
          Object.keys(reports).forEach(key => {
            this.customReports.push({
              text: key,
              children: reports[key].sort((a, b) => a.rankOrder - b.rankOrder)
            });
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    isEmpty(value) {
      if (typeof value === "string") {
        return value === null || value === undefined || value.trim().length == 0;
      }
      return value === null || value === undefined;
    },

    breadcrumbs() {
      return this.$store.dispatch("setBreadcrumb", [
        {
          text: this.$i18n.translate("Dashboard"),
          to: { name: "dashboard" },
          exact: true
        },
        { text: "Reports" }
      ]);
    }
  },
  mounted() {
    this.breadcrumbs();
    this.fetchCustomReports().then(() => {
      this.fetchFavoriteReports();
    });
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram", "selectedParticipant"])
  }
};
</script>
